import { useCallback, useEffect, useMemo, useState } from "react";
import { BellRing, Bot, CalendarDays, Gift, Loader2, MessageSquare, PauseCircle, Play, RefreshCw, Sparkles, Trophy, Vote } from "lucide-react";
import PublicSiteLayout, { PageHero, PublicCard, PublicLinkButton, PUBLIC_LINKS } from "../components/PublicSiteLayout";
import { api } from "../lib/api";
import { CurrencyIcon, currencyAmountLabel, currencyLabel, visibleCurrencies as getVisibleCurrencies } from "../lib/currencyDisplay";
import { getViewerSession } from "../lib/viewerSession";
import { viewerRequestStatusHelp, viewerRequestStatusLabel, viewerRequestStatusTone } from "../lib/viewerRequestStatus";

const futureRedeems = [
  { icon: BellRing, title: "Sound alerts", body: "Drop a perfectly timed noise into the stream when the moment deserves it." },
  { icon: Trophy, title: "Stream challenges", body: "Push Parker into community goals, game challenges, and questionable tactical decisions." },
  { icon: Vote, title: "Viewer polls", body: "Help steer bits, games, events, punishments, rewards, and other stream moments." },
  { icon: Gift, title: "Giveaway entries", body: "Future event and giveaway entries will have a clearer home here." },
  { icon: CalendarDays, title: "Community events", body: "Special nights, raids, collabs, and Ratterday Saints participation hooks." },
  { icon: Bot, title: "AI and BarracksBunny chaos", body: "The future tool layer can power weirder interactive moments without making this a software page." },
];

export default function Redeems() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [balances, setBalances] = useState({});
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesError, setBalancesError] = useState("");
  const [error, setError] = useState("");
  const [submittingId, setSubmittingId] = useState("");
  const [submitMessage, setSubmitMessage] = useState("");
  const [latestRequest, setLatestRequest] = useState(null);
  const [inputValues, setInputValues] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [{ session: viewerSession }, { data: interactions }] = await Promise.all([
        getViewerSession(),
        api.get("/interactions"),
      ]);
      setSession(viewerSession);
      setData(interactions);
      if (viewerSession) {
        setBalancesLoading(true);
        setBalancesError("");
        try {
          const balanceData = await fetchViewerBalances(viewerSession);
          setBalances(balanceData.balances || {});
        } catch (balanceErr) {
          setBalances({});
          setBalancesError("Could not load balances.");
        } finally {
          setBalancesLoading(false);
        }
      } else {
        setBalances({});
        setBalancesError("");
        setBalancesLoading(false);
      }
    } catch (err) {
      setError("Redeems could not be loaded right now. Check back during stream or join Discord for updates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const currencyMap = useMemo(() => {
    const map = {};
    (data?.currencies || []).forEach((currency) => { map[currency.id] = currency; });
    return map;
  }, [data]);
  const visibleCurrencies = useMemo(
    () => getVisibleCurrencies(data?.currencies || []),
    [data]
  );

  const categoryMap = useMemo(() => {
    const map = {};
    (data?.interaction_categories || []).forEach((category) => { map[category.slug] = category; });
    return map;
  }, [data]);

  const publicInteractions = useMemo(
    () => data?.interaction_definitions || [],
    [data]
  );
  const redeemGroups = useMemo(
    () => groupInteractionsByCategory(publicInteractions, categoryMap),
    [publicInteractions, categoryMap]
  );

  const submitRedeem = async (interaction, viewerInput = "") => {
    if (data?.interactions_paused) {
      setSubmitMessage("Interactions are paused right now.");
      setLatestRequest(null);
      return;
    }
    if (!session) {
      setSubmitMessage("Sign in first, then try the redeem again.");
      setLatestRequest(null);
      return;
    }
    setSubmittingId(interaction.id);
    setSubmitMessage("");
    setLatestRequest(null);
    try {
      const requester = session.twitch_user_id || session.platform_user_id || session.login;
      const { data: request } = await api.post("/interaction-requests", {
        interaction_id: interaction.id,
        requester_id: String(requester || "").trim(),
        source: "public_site_test",
        input_values: {
          requester_display_name: session.display_name || session.login || "Viewer",
          requester_login: session.login || "",
          viewer_input: viewerInput,
        },
      });
      setLatestRequest({
        ...request,
        interaction_name: request.interaction_name || interaction.name,
      });
      if (request.charge_status === "charged") {
        const currency = currencyMap[request.charged_currency_id] || { id: request.charged_currency_id };
        setSubmitMessage(`Redeem submitted.\n${currencyAmountLabel(request.charged_amount, currency)} charged.\nRequest ID: ${request.id || request.request_id}.`);
        try {
          const balanceData = await fetchViewerBalances(session);
          setBalances(balanceData.balances || {});
        } catch (balanceErr) {
          setBalancesError("Could not load balances.");
        }
      } else {
        setSubmitMessage("Redeem submitted. The request is now in the stream queue.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        setSubmitMessage(formatInsufficientBalanceMessage(detail, currencyMap));
      } else if (typeof detail === "object" && detail?.message) {
        setSubmitMessage(detail.message);
      } else if (typeof detail === "string") {
        setSubmitMessage(detail);
      } else {
        setSubmitMessage("That redeem could not be submitted right now.");
      }
      setLatestRequest(null);
    } finally {
      setSubmittingId("");
    }
  };

  return (
    <PublicSiteLayout>
      <div className="redeems-page">
      <PageHero
        eyebrow="Early access redeems"
        title="Viewer-powered stream chaos."
        body="Public redeems are available for early viewer testing. Some are free, some require currency, and more earning methods are rolling out over time."
        actions={(
          <>
            <PublicLinkButton href={session ? "/profile" : "/login"} primary><Sparkles className="w-4 h-4" /> {session ? "Open profile" : "Sign in to redeem"}</PublicLinkButton>
            <PublicLinkButton href={PUBLIC_LINKS.discord}><MessageSquare className="w-4 h-4" /> Join Discord</PublicLinkButton>
          </>
        )}
      />

      <section className="redeems-catalog">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Available now</div>
            <h2 className="text-2xl font-black uppercase tracking-normal text-[hsl(var(--foreground))]">Public interaction queue</h2>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-3xl">
              These buttons create interaction requests for enabled public redeems. Paid redeems are charged when submitted, and manual rejection refunds update your balance.
            </p>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-3xl">
              Currency is still being tuned for early access. Check your Profile for current balances and viewer preferences.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="public-quiet-action"
          >
            <RefreshCw size={16} aria-hidden="true" /> {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {data?.interactions_paused && (
          <div className="mt-5 rounded border border-amber-400/45 bg-amber-400/10 px-4 py-3 flex items-center gap-2 text-amber-100">
            <PauseCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">Interactions Paused</span>
          </div>
        )}

        {!session && (
          <div className="mt-5 rounded border border-[hsl(var(--currency-event))]/40 bg-[hsl(var(--currency-event))]/10 px-4 py-3 text-sm text-[hsl(var(--foreground))]">
            Sign in with Twitch or Kick to submit redeems as your viewer account.
            <div className="mt-3">
              <PublicLinkButton href="/login?return_to=%2Fredeems" primary><Sparkles className="w-4 h-4" /> Sign in</PublicLinkButton>
            </div>
          </div>
        )}
        {submitMessage && (
          <div role="status" className="mt-5 rounded border border-[hsl(var(--border))] bg-black/50 px-4 py-3 text-sm text-[hsl(var(--foreground))]">
            <div className="whitespace-pre-line">{submitMessage}</div>
            {session && (
              <div className="mt-3">
                <PublicLinkButton href="/profile" primary><Sparkles className="w-4 h-4" /> Open Profile</PublicLinkButton>
              </div>
            )}
          </div>
        )}
        {latestRequest && <LatestRequestCard request={latestRequest} />}
        {error && (
          <div role="alert" className="mt-5 rounded border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        )}

        <div className="mt-5 space-y-8">
          {session && (
            <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-4">
              <BalanceSummary
                currencies={visibleCurrencies}
                balances={balances}
                loading={balancesLoading}
                error={balancesError}
              />
              <EarningInfoCard />
            </div>
          )}
          {loading && <div className="text-sm text-[hsl(var(--muted-foreground))]">Loading redeems...</div>}
          {!loading && !error && publicInteractions.length === 0 && (
            <EmptyRedeemsState />
          )}
          {redeemGroups.map((group) => (
            <RedeemGroup
              key={group.key}
              group={group}
              currencyMap={currencyMap}
              categoryMap={categoryMap}
              balances={balances}
              session={session}
              submittingId={submittingId}
              interactionsPaused={data?.interactions_paused}
              inputValues={inputValues}
              onInputChange={(interactionId, value) => setInputValues((current) => ({ ...current, [interactionId]: value }))}
              onSubmit={submitRedeem}
            />
          ))}
        </div>
      </section>

      <section className="redeems-planned mt-12">
        <div className="label-eyebrow mb-3 text-[hsl(var(--currency-event))]">Still planned</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {futureRedeems.map((redeem) => (
            <PublicCard key={redeem.title} icon={redeem.icon} title={redeem.title} body={redeem.body} />
          ))}
        </div>
      </section>

      <section className="redeems-notes mt-8 grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-6">
        <div className="surface p-6 bg-black/70">
          <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Accounts and rewards later</div>
          <h2 className="text-2xl font-black uppercase tracking-normal text-[hsl(var(--foreground))]">Currency and profiles are in early access.</h2>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            Paid redeems can show costs, check balances, charge on submission, and apply manual rejection refunds to your balance.
          </p>
          <div className="mt-5">
            <PublicLinkButton href={session ? "/profile" : "/login"} primary>
              <Sparkles className="w-4 h-4" /> {session ? "View Profile" : "Sign in to redeem"}
            </PublicLinkButton>
          </div>
        </div>

        <div className="surface p-6 bg-black/70">
          <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Watch live</div>
          <h2 className="text-2xl font-black uppercase tracking-normal text-[hsl(var(--foreground))]">Come hang out while this gets wired up.</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <PublicLinkButton href={PUBLIC_LINKS.twitch} primary><Play className="w-4 h-4" /> Watch on Twitch</PublicLinkButton>
            <PublicLinkButton href={PUBLIC_LINKS.discord}><MessageSquare className="w-4 h-4" /> Join Discord</PublicLinkButton>
          </div>
        </div>
      </section>
      </div>
    </PublicSiteLayout>
  );
}

function groupInteractionsByCategory(interactions, categoryMap) {
  const groups = new Map();
  interactions.forEach((interaction) => {
    const firstTag = (interaction.category_tags || [])[0] || interaction.category || "";
    const category = categoryMap[firstTag] || (firstTag ? { slug: firstTag, name: cleanCategoryName(firstTag) } : null);
    const key = category?.slug || firstTag || "other";
    const name = category?.name || "Other Redeems";
    if (!groups.has(key)) {
      groups.set(key, { key, name, interactions: [] });
    }
    groups.get(key).interactions.push(interaction);
  });
  return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function cleanCategoryName(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Other Redeems";
  return raw.replace(/[_-]/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function RedeemGroup({ group, currencyMap, categoryMap, balances, session, submittingId, interactionsPaused, inputValues, onInputChange, onSubmit }) {
  if (group.interactions.length === 0) return null;
  return (
    <section className="redeem-group" aria-label={group.name}>
      <div className="redeem-group-heading">
        <h3>{group.name}</h3>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">
          {group.interactions.length} {group.interactions.length === 1 ? "redeem" : "redeems"}
        </span>
      </div>
      <div className="redeem-grid">
        {group.interactions.map((interaction) => {
          const availability = redeemAvailability(interaction, session, submittingId, interactionsPaused);
          return (
            <RedeemCard
              key={interaction.id}
              interaction={interaction}
              currency={currencyMap[interaction.cost_type] || { id: interaction.cost_type }}
              categoryMap={categoryMap}
              availability={availability}
              submitting={submittingId === interaction.id}
              balance={balances?.[interaction.cost_type]}
              inputValue={inputValues?.[interaction.id] || ""}
              onInputChange={(value) => onInputChange(interaction.id, value)}
              onSubmit={onSubmit}
            />
          );
        })}
      </div>
    </section>
  );
}

function redeemAvailability(interaction, session, submittingId, interactionsPaused) {
  if (interactionsPaused) return { disabled: true, message: "Interactions paused" };
  if (submittingId) return { disabled: true, message: "Submitting another redeem..." };
  if (!session) return { disabled: true, message: "Sign in to redeem" };
  if (interaction.enabled === false || interaction.public === false) return { disabled: true, message: "Currently unavailable" };
  return { disabled: false, message: "" };
}

function EmptyRedeemsState() {
  return (
    <div className="rounded border border-dashed border-[hsl(var(--border))] bg-black/40 px-4 py-6 text-sm text-[hsl(var(--muted-foreground))]">
      No redeems are available right now. Check back during stream or join Discord for updates.
    </div>
  );
}

async function fetchViewerBalances(session) {
  const viewerId = session?.twitch_user_id || session?.platform_user_id;
  if (!viewerId) return { balances: {} };
  const { data } = await api.get(`/currency/viewers/${encodeURIComponent(viewerId)}/balances`, {
    params: {
      login: session?.login || session?.display_name || viewerId,
    },
  });
  return data;
}

function BalanceSummary({ currencies, balances, loading, error }) {
  return (
    <div className="redeems-balance-summary">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
        <div>
          <div className="label-eyebrow text-[hsl(var(--currency-event))]">Your balance</div>
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Paid redeems compare their cost against these balances before submitting.</p>
        </div>
        <a href="/profile" className="text-xs uppercase tracking-widest text-[hsl(var(--foreground))] hover:text-[hsl(var(--currency-event))] transition">Profile</a>
      </div>
      {loading ? (
        <div className="text-sm text-[hsl(var(--muted-foreground))]">Loading balances...</div>
      ) : error ? (
        <div className="text-sm text-red-100">Could not load balances.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {currencies.length === 0 ? (
            <span className="text-sm text-[hsl(var(--muted-foreground))]">No balances yet.</span>
          ) : currencies.map((currency) => (
            <span key={currency.id} className="inline-flex items-center gap-2 rounded bg-white/[0.04] px-3 py-2 text-sm text-[hsl(var(--foreground))]">
              <CurrencyIcon currency={currency} className="w-4 h-4" />
              <span className="font-mono">{balances?.[currency.id] ?? 0}</span>
              <span>{currencyLabel(currency)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function EarningInfoCard() {
  const opportunities = [
    "Stream participation",
    "Community events",
    "Giveaways",
    "Future channel integrations",
  ];
  return (
    <div className="redeems-earning-info">
      <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Need more currency?</div>
      <h3 className="text-lg font-black uppercase tracking-normal text-[hsl(var(--foreground))]">Earning methods are being tuned.</h3>
      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
        Future earning opportunities may come from community participation.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {opportunities.map((opportunity) => (
          <span key={opportunity} className="rounded bg-white/[0.04] px-2.5 py-1 text-xs text-[hsl(var(--muted-foreground))]">
            {opportunity}
          </span>
        ))}
      </div>
    </div>
  );
}

function LatestRequestCard({ request }) {
  const status = request.status || "pending";
  const tone = viewerRequestStatusTone(status);
  const color = tone === "success"
    ? "border-emerald-500 text-emerald-300 bg-emerald-500/10"
    : tone === "danger"
      ? "border-red-500 text-red-200 bg-red-500/10"
      : tone === "warning"
        ? "border-amber-400 text-amber-200 bg-amber-500/10"
        : "border-[hsl(var(--currency-event))] text-[hsl(var(--foreground))] bg-[hsl(var(--currency-event))]/10";
  return (
    <div className="mt-5 rounded border border-[hsl(var(--currency-event))]/50 bg-black/60 p-4">
      <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Latest request</div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-[hsl(var(--foreground))] break-words">{request.interaction_name || "Submitted redeem"}</div>
          {request.id && <div className="mt-1 font-mono text-[11px] text-[hsl(var(--muted-foreground))] break-all">Request ID: {request.id}</div>}
        </div>
        <span className={`inline-flex w-fit px-2.5 py-1 rounded border text-xs font-medium ${color}`}>
          {viewerRequestStatusLabel(status)}
        </span>
      </div>
      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">{viewerRequestStatusHelp(status)}</p>
    </div>
  );
}

function RedeemCard({ interaction, currency, categoryMap, availability, submitting, balance, inputValue, onInputChange, onSubmit }) {
  const accentColor = interaction.accent_color || "#bf5af2";
  const tags = (interaction.category_tags || []).map((slug) => categoryMap[slug] || { slug, name: slug, color: "hsl(var(--muted-foreground))" });
  const costAmount = Number(interaction.cost_amount || 0);
  const isFree = costAmount <= 0;
  const costLabel = isFree ? "Free redeem" : `Requires ${currencyAmountLabel(costAmount, currency)}`;
  const viewerInput = interactionViewerInputConfig(interaction);
  const needsViewerInput = viewerInput.required;
  const inputMissing = needsViewerInput && !String(inputValue || "").trim();
  const submitDisabled = availability.disabled || inputMissing;
  return (
    <article className="redeem-card" style={{ "--redeem-accent": accentColor }}>
      <div className="flex items-start gap-3">
        <div className="redeem-icon">
          <CurrencyIcon currency={{ icon: interaction.icon, color: accentColor }} className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h4 className="text-lg font-semibold leading-tight text-[hsl(var(--foreground))] break-words">{interaction.name}</h4>
          {interaction.description && <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{interaction.description}</p>}
        </div>
      </div>

      {tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span key={tag.slug} className="redeem-tag">
              {tag.name}
            </span>
          ))}
        </div>
      )}

      <div className={`redeem-cost${isFree ? " is-free" : ""}`}>
        <div className="flex items-center gap-2">
          {!isFree && <CurrencyIcon currency={currency} className="w-4 h-4" />}
          <span className="font-semibold">{costLabel}</span>
        </div>
        {!isFree && (
          <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
            Current balance: {Number(balance || 0)} {currencyLabel(currency)}. Paid redeems are charged when submitted.
          </div>
        )}
      </div>

      {needsViewerInput && (
        <label className="mt-4 block">
          <span className="label-eyebrow mb-1.5 block text-[hsl(var(--currency-event))]">{viewerInput.label}</span>
          <textarea
            value={inputValue}
            onChange={(event) => onInputChange(event.target.value)}
            maxLength={viewerInput.maxLength}
            rows={3}
            className="w-full bg-black/45 border border-[hsl(var(--border))] rounded px-3 py-2 text-sm text-[hsl(var(--foreground))] outline-none focus:border-[hsl(var(--currency-event))]"
            placeholder={viewerInput.placeholder}
          />
        </label>
      )}

      <div className="redeem-action-row">
        <div className="text-sm text-[hsl(var(--muted-foreground))]">
          {inputMissing ? `Add ${viewerInput.label.toLowerCase()}` : availability.message || (isFree ? "No balance needed" : "Balance required")}
        </div>
        <button
          type="button"
          onClick={() => onSubmit(interaction, inputValue)}
          disabled={submitDisabled}
          aria-label={`Redeem ${interaction.name}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[hsl(var(--currency-event))] text-white text-sm font-medium hover:brightness-110 disabled:bg-black/50 disabled:border disabled:border-[hsl(var(--border))] disabled:text-[hsl(var(--muted-foreground))] disabled:opacity-80 disabled:cursor-not-allowed"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Redeem
        </button>
      </div>
    </article>
  );
}

function interactionNeedsViewerInput(interaction) {
  return interactionViewerInputConfig(interaction).required;
}

function interactionViewerInputConfig(interaction) {
  const fallback = {
    required: false,
    label: "Viewer input",
    placeholder: "Type your message",
    maxLength: 280,
  };
  return (interaction.actions || []).some((action) => {
    const config = action.config || {};
    if (action.action_type === "ai_generate_text") {
      return config.requires_viewer_input === true
        || config.prompt_mode === "viewer_topic"
        || promptUsesViewerInput(config.prompt_template);
    }
    if (action.action_type === "tts_generate_audio") {
      return config.text_mode === "user_input" || config.input_text_key === "viewer_input" || config.input_text_key === "user_input" || config.input_text_key === "requester_input";
    }
    if (action.action_type === "tts") {
      return config.text_source === "requester_input" || config.text_source === "viewer_input" || config.text_source === "user_input";
    }
    return false;
  }) ? viewerInputConfigFromActions(interaction.actions, fallback) : fallback;
}

function viewerInputConfigFromActions(actions, fallback) {
  for (const action of actions || []) {
    const config = action.config || {};
    if (action.action_type === "ai_generate_text" && (config.requires_viewer_input === true || config.prompt_mode === "viewer_topic" || promptUsesViewerInput(config.prompt_template))) {
      const label = String(config.viewer_input_label || "Topic").trim() || "Topic";
      return {
        required: true,
        label,
        placeholder: label.toLowerCase() === "topic" ? "Type a topic, like haunted cupcakes" : `Type ${label.toLowerCase()}`,
        maxLength: Number(config.viewer_input_max_length || 280),
      };
    }
    if (action.action_type === "tts_generate_audio" && (config.text_mode === "user_input" || config.input_text_key === "viewer_input" || config.input_text_key === "user_input" || config.input_text_key === "requester_input")) {
      return {
        required: true,
        label: "Message for TTS",
        placeholder: "Type what Bunny should say",
        maxLength: Number(config.max_character_length || 280),
      };
    }
    if (action.action_type === "tts" && (config.text_source === "requester_input" || config.text_source === "viewer_input" || config.text_source === "user_input")) {
      return {
        required: true,
        label: "Message for TTS",
        placeholder: "Type what Bunny should say",
        maxLength: Number(config.max_character_length || 280),
      };
    }
  }
  return fallback;
}

function promptUsesViewerInput(promptTemplate) {
  const prompt = String(promptTemplate || "");
  return /\{\{?\s*(viewer_input|user_input|requester_input|topic)\s*\}?\}/i.test(prompt);
}

function formatInsufficientBalanceMessage(detail, currencyMap) {
  if (typeof detail === "object" && detail) {
    const currency = currencyMap[detail.currency_id] || { id: detail.currency_id };
    const name = detail.currency_id ? currencyLabel(currency) : "currency";
    const required = detail.required ?? "?";
    const balance = detail.balance ?? "?";
    return `Not enough ${name}.\nRequired: ${required}\nCurrent Balance: ${balance}`;
  }
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  return "Not enough currency for that redeem.";
}
